import type { DateTime } from "luxon";
import { type FC, useCallback } from "react";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { staffingDayKey } from "../gapsRequests";
import type { GapsHomeModel } from "../useGapsDayModel";
import { type GapsSheetIntent, GapsSheetKind, intentForHomeDay, moveGapsSheetToDay, openGapsSheet, useGapsSheet } from "../useGapsSheet";
import { GapsAddStaffSheet } from "./GapsAddStaffSheet";
import { GapsAssignUnitSheet } from "./GapsAssignUnitSheet";
import { GapsCallInSheet } from "./GapsCallInSheet";
import { GapsPostShiftSheet } from "./GapsPostShiftSheet";
import { GapsRequestSheets } from "./GapsRequestSheets";
import { GapsStaffSheet } from "./GapsStaffSheet";

type Props = {
	home: GapsHomeModel;
	/** The day the home is showing. */
	date: DateTime;
	/** Moves the home to another day — what a page's Date row does, the page coming along (`moveGapsSheetToDay`). */
	onMoveDay: (date: DateTime) => void;
};

/** Where a post sheet was opened from, as `gaps_post_shift_opened` records it (the group `+` and the bar log their own). */
enum PostOpenedSource {
	CoverPrompt = "cover_prompt",
	PostInstead = "add_post_instead",
	PrefillChip = "prefill_chip",
}

/**
 * The action sheets the gaps-first home hosts, each opened by an intent in the shared sheet store (`useGapsSheet`):
 * a staff row's actions, the assign-unit picker, Log a call-in, Post a shift, Add someone, and — for the Posted tab — a
 * posted request's actions and the approve confirm (`GapsRequestSheets`, which Open Shifts hosts too). The store is how a
 * trigger outside the page — the bottom bar's Post, More's quick actions — reaches them.
 *
 * All of them stay mounted and are driven open and shut by the intent, so each can play its exit animation instead of
 * vanishing mid-frame when the intent clears. Opening one replaces whichever was up: moving from one to the next (the
 * cover prompt's "Post a shift", an add's "Post it instead", an unplaced add's assign-unit) is a write to the store.
 *
 * Only a sheet opened for THIS day is drawn (`intentForHomeDay`): when the home's day moves under an open sheet, that
 * sheet goes down in the same render, and `useCloseGapsSheetOffDay` then closes its intent. Each sheet closes only its
 * own opening, so a write that finishes after the user moved on closes nothing they moved to.
 */
export const GapsSheets: FC<Props> = ({ home, date, onMoveDay }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { intent: storedIntent } = useGapsSheet();
	const departmentId = chosenDepartment.department_id;
	const dayISO = staffingDayKey(date);
	const intent = intentForHomeDay(storedIntent, dayISO);

	/** Every post sheet opened from another sheet: prefilled from a group of this day, and recorded as opened. */
	const openPostFrom = useCallback(
		(source: PostOpenedSource, groupKey: string): void => {
			track("gaps_post_shift_opened", { department_id: departmentId, group: groupKey, source });
			openGapsSheet({ kind: GapsSheetKind.PostShift, dayISO, groupKey });
		},
		[departmentId, dayISO, track],
	);

	// The cover prompt's "Post a shift": the post sheet, prefilled from the group the removal left short.
	const handleCoverPost = useCallback(
		(groupKey: string): void => {
			track("gaps_cover_prompt_post_clicked", { department_id: departmentId, group: groupKey });
			openPostFrom(PostOpenedSource.CoverPrompt, groupKey);
		},
		[departmentId, openPostFrom, track],
	);

	// A page's Date row: the page follows the home to the picked day as the same opening, so nothing typed is lost.
	const handleMoveDay = useCallback(
		(from: GapsSheetIntent, next: DateTime): void => {
			if (!moveGapsSheetToDay(from, staffingDayKey(next))) return;
			track("gaps_page_date_changed", { department_id: departmentId, sheet: from.kind, days_ahead: Math.round(next.diff(date.startOf("day"), "days").days) });
			onMoveDay(next);
		},
		[departmentId, date, onMoveDay, track],
	);

	const handlePrefillFrom = useCallback((groupKey: string): void => openPostFrom(PostOpenedSource.PrefillChip, groupKey), [openPostFrom]);

	const handlePostInstead = useCallback(
		(groupKey: string): void => {
			track("gaps_add_staff_post_instead", { department_id: departmentId, group: groupKey });
			openPostFrom(PostOpenedSource.PostInstead, groupKey);
		},
		[departmentId, openPostFrom, track],
	);

	// A day that is over offers no Post anywhere — nobody could pick it up (APP-886) — so the cover prompt and Add's
	// "Post instead" lose theirs there.
	const coverPost = home.isDayPast ? undefined : handleCoverPost;

	return (
		<>
			{/* The store's own intent objects, not copies: a new object is a new opening (see `useSheetOpening`), and it is
			    what each sheet's own close is bound to. */}
			<GapsStaffSheet target={intent?.kind === GapsSheetKind.StaffActions ? intent : null} home={home} onPostShift={coverPost} />
			<GapsAssignUnitSheet target={intent?.kind === GapsSheetKind.AssignUnit ? intent : null} home={home} />
			<GapsCallInSheet
				target={intent?.kind === GapsSheetKind.CallIn ? intent : null}
				home={home}
				date={date}
				onPostShift={coverPost}
				onMoveDay={handleMoveDay}
			/>
			<GapsPostShiftSheet
				target={intent?.kind === GapsSheetKind.PostShift ? intent : null}
				home={home}
				date={date}
				onPrefillFrom={handlePrefillFrom}
				onMoveDay={handleMoveDay}
			/>
			<GapsAddStaffSheet
				target={intent?.kind === GapsSheetKind.AddStaff ? intent : null}
				home={home}
				date={date}
				onPostInstead={home.isDayPast ? undefined : handlePostInstead}
				onMoveDay={handleMoveDay}
			/>
			{/* The Posted tab's sheets: a posted request's actions and the approve confirm (shared with Open Shifts). */}
			<GapsRequestSheets />
		</>
	);
};
